// Mezcla el torneo que hay en memoria con el que acaba de devolver el servidor.
//
// Cada resultado se guarda con un PATCH que escribe un solo partido y contesta con
// el torneo entero ya actualizado. Ese torneo trae, además de lo que se acaba de
// grabar, los resultados que cargaron los otros dispositivos: meterlo en memoria es
// lo que mantiene la pantalla al día durante una fecha.
//
// No se puede reemplazar el torneo en memoria a secas por dos razones:
//
//  1. `mapToViewModel` regenera los ids de cliente en cada lectura, y la navegación
//     (rueda/zona/fecha) los referencia: cambiarlos haría saltar la guarda de
//     entidad huérfana y sacaría al usuario de la pantalla en la que está. Por eso
//     los ids de cliente que quedan son los que ya estaban en memoria.
//
//  2. Los resultados que este dispositivo cargó y todavía no confirmó el servidor
//     son más nuevos que la respuesta que está llegando (que se armó antes de
//     mandarlos): esos partidos se conservan como están en memoria.
//
// El cruce entre las dos copias se hace por el _id de Mongo del subdocumento, que
// es lo único estable entre dos lecturas del mismo torneo.

const recorrerEntidades = (torneo, visitar) =>
  (torneo.ruedas || []).forEach((rueda) => {
    visitar(rueda);
    (rueda.zones || []).forEach((zona) => {
      visitar(zona);
      (zona.matchDates || []).forEach((fecha) => {
        visitar(fecha);
        (fecha.matches || []).forEach(visitar);
      });
    });
  });

const indexarPorId = (torneo) => {
  const porId = new Map();
  recorrerEntidades(torneo, (entidad) => {
    if (entidad && entidad._id) porId.set(entidad._id, entidad);
  });
  return porId;
};

// `pendientes` es un Set con los _id de los partidos que este dispositivo cargó y
// todavía no confirmó el servidor.
export function mezclarTorneo(local, remoto, pendientes) {
  const porId = indexarPorId(local);
  // Lo que trae el servidor, pero con el id de cliente que esa misma entidad ya
  // tenía en memoria. Si es algo que acá no existía (una fecha nueva cargada desde
  // el ABM en otro dispositivo) se queda con el suyo.
  const conIdLocal = (remota) => {
    const enMemoria = porId.get(remota._id);
    return enMemoria ? { ...remota, id: enMemoria.id } : remota;
  };

  return {
    ...remoto,
    ruedas: (remoto.ruedas || []).map((rueda) => ({
      ...conIdLocal(rueda),
      zones: (rueda.zones || []).map((zona) => ({
        ...conIdLocal(zona),
        matchDates: (zona.matchDates || []).map((fecha) => ({
          ...conIdLocal(fecha),
          matches: (fecha.matches || []).map((partido) => {
            const enMemoria = porId.get(partido._id);
            if (enMemoria && pendientes.has(partido._id)) return enMemoria;
            return conIdLocal(partido);
          }),
        })),
      })),
    })),
  };
}
